import React from 'react'

import ICON_MORE_VERTICAL from '@public/assets/common/icon_more_vertical.svg'
import REASSIGN_ICON from '@public/assets/icon_reassign.svg'
import TIMELINE_ICON from '@public/assets/action/icon_timeline_black.svg'

import type { TutorialStepContentProps } from '../types'

const textClassName = 'text-base font-normal leading-[1.6] text-[#666666]'
const listClassName = 'm-0 list-disc pl-5'
const semiboldClassName = 'font-semibold'

function ActivitiesButtonPreview() {
  return (
    <span className="inline-flex h-6 items-center justify-center gap-1 rounded-[40px] border border-[#d9d9d9] bg-white px-3 align-middle text-[13px] leading-none text-[#333333]">
      <img className="h-3.5 w-[15px]" src={TIMELINE_ICON} alt="" />
      <span>2</span>
    </span>
  )
}

function ReassignButtonPreview() {
  return (
    <span className="inline-flex size-6 items-center justify-center rounded-[40px] border border-[#d9d9d9] bg-white align-middle">
      <img className="size-3.5" src={REASSIGN_ICON} alt="" />
    </span>
  )
}

export function TutorialStepContent({ stepId }: TutorialStepContentProps) {
  switch (stepId) {
    case 'create-suggested-entry':
      return (
        <div className={textClassName}>
          <p>If a suggested entry looks right:</p>
          <ul className={listClassName}>
            <li>
              Click the{' '}
              <span className={semiboldClassName}>suggested matter</span> for
              that group.
            </li>
            <li>
              A new time entry is created using all activities in that group.
            </li>
          </ul>
          <p>
            This is the quickest way to post time when everything already
            belongs together.
          </p>
        </div>
      )
    case 'remove-activities':
      return (
        <div className={textClassName}>
          <p>If a group includes activities you don&apos;t want:</p>
          <ul className={listClassName}>
            <li>
              Click the activity&apos;s overflow menu{' '}
              <img
                className="inline size-3.5 align-middle"
                src={ICON_MORE_VERTICAL}
                alt=""
              />
              .
            </li>
            <li>
              Select{' '}
              <span className={semiboldClassName}>Remove from group</span>.
            </li>
          </ul>
          <p>
            The activity is simply removed from that suggested entry—it&apos;s
            not deleted and can be used later for a different group or matter.
          </p>
        </div>
      )
    case 'move-activities':
      return (
        <div className={textClassName}>
          <p>If an activity belongs in a different group:</p>
          <ul className={listClassName}>
            <li>
              Drag and drop one or more activities into another suggested entry
              group.
            </li>
          </ul>
          <p>This lets you refine groupings before creating time entries.</p>
        </div>
      )
    case 'combine-groups':
      return (
        <div className={textClassName}>
          <p>
            If activities across different groups belong to the same matter:
          </p>
          <ul className={listClassName}>
            <li>
              Select the activities you want (from one or multiple groups).
            </li>
            <li>
              Click a{' '}
              <span className={semiboldClassName}>suggested matter number</span>{' '}
              to create a time entry for that matter using the selected
              activities.
            </li>
          </ul>
          <p>This is useful when work is related but captured separately.</p>
        </div>
      )
    case 'drag-to-time-entry':
      return (
        <div className={textClassName}>
          <p>You can also create time entries manually:</p>
          <ul className={listClassName}>
            <li>Select activities from one group or across multiple groups.</li>
            <li>
              Drag them into the{' '}
              <span className={semiboldClassName}>Time Entry panel</span>.
            </li>
          </ul>
          <p>
            <span className={semiboldClassName}>Tip</span>: If you don&apos;t
            see the matter you want,
          </p>
          <ul className={listClassName}>
            <li>
              Click <span className={semiboldClassName}>Recent matters</span> or{' '}
              <span className={semiboldClassName}>Search matter</span> to create
              a matter workspace.
            </li>
            <li>
              Then drag the activities into that matter&apos;s time entry space.
            </li>
          </ul>
        </div>
      )
    case 'merge-activities':
      return (
        <div className={textClassName}>
          <p>If you want to add activities to an existing entry:</p>
          <ul className={listClassName}>
            <li>Select one or more activities.</li>
            <li>Select the destination time entry.</li>
            <li>
              Click <span className={semiboldClassName}>Merge</span> to combine
              them into the selected time entry.
            </li>
          </ul>
          <p>This helps keep entries consolidated and clean.</p>
        </div>
      )
    case 'reassign-activities':
      return (
        <div className="text-base font-normal leading-[1.35] tracking-[0.5px] text-[#666666]">
          <p>
          You can reassign some or all activities to a different time entry, split the existing time entry, or move the activities to a different matter.
          </p>
          <ul className="m-0 list-disc pl-6">
            <li>
              Click the Activities button <ActivitiesButtonPreview /> on a time
              entry to open the timeline panel.
            </li>
            <li>
              Select one or more activities you want to move to a new time
              entry.
            </li>
            <li>
              Click <span className={semiboldClassName}>Reassign</span>{' '}
              <ReassignButtonPreview />. MIRA will create a new time entry using
              the selected activities:
              <ul className="m-0 list-disc pl-6">
                <li>
                  Select a{' '}
                  <span className={semiboldClassName}>different matter</span>:
                  Moves the activities to the new matter and keeps the original
                  entry unchanged.
                </li>
                <li>
                  Select the{' '}
                  <span className={semiboldClassName}>same matter</span>:
                  Separates the activities into a new time entry under the same
                  matter.
                </li>
              </ul>
            </li>
          </ul>
        </div>
      )
  }
}
